import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    APIResponse,
    DataTableParam,
    Filter,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    FilterFieldType,
    FilterOperatorType,
    TableFilterConfig,
} from '@core/interfaces/system/system-common';
import {
    EconomicsOverTimeResponse,
    Project,
    ProjectAssetDto,
    ProjectClone,
    ProjectCostAnalysis,
    ProjectCreate,
    ProjectDto,
    ProjectService,
    ProjectUpdate,
} from '@core/interfaces/engin/program-management/project';
import {ProjectApi} from '../../api/program-management/project.api';
import {map} from 'rxjs/operators';
import {DownloadURL} from '@core/interfaces/engin/load-forecast/load-forecast';
import {
    SpatialCardMetricRequest,
    SpatialCardResponseMetric,
} from '@theme/components/card-components/spatial-card/model/model-spatial-card';
import {TableService} from '@core/utils/table.service';
import {AssetColumn} from '@core/interfaces/common/asset';
import {AlternativeScenarioOption} from '@core/interfaces/engin/program-management/alternative';

@Injectable()
export class ProjectServiceImpl extends ProjectService {
    constructor(private api: ProjectApi) {
        super();
    }

    /*
     * APIs for Projects model
     */
    public createProject(req: ProjectCreate): Observable<APIResponse<Project>> {
        return this.api.createProject(req);
    }

    public updateProject(projectId: number, req: ProjectUpdate): Observable<APIResponse<Project>> {
        return this.api.updateProject(projectId, req);
    }

    public deleteProject(projectId: number): Observable<APIResponse<boolean>> {
        return this.api.deleteProject(projectId);
    }

    public cloneProject(
        req: ProjectClone,
        workflowItemId: number,
        projectId: number,
    ): Observable<APIResponse<Project>> {
        return this.api.cloneProject(req, workflowItemId, projectId);
    }
    public getProject(projectId: number): Observable<APIResponse<Project>> {
        return this.api.getProject(projectId);
    }

    public getProjectList(filters?: Filter[], pageSize?: number): Observable<Project[]> {
        return this.api.getProjectList(filters, pageSize).pipe(map((result) => result.items));
    }

    public getProjectsByField(field: {key: string; value: string}): Observable<Project[]> {
        return this.api.getProjectsByField(field);
    }

    /*
     * APIs for ProjectDto
     */
    public getProjectDto(workflowItemId: number, projectId: number): Observable<APIResponse<ProjectDto>> {
        return this.api.getProjectDto(workflowItemId, projectId);
    }

    public getProjectDtoList(workflowItemId: number, params?: Filter[], pageSize?: number): Observable<ProjectDto[]> {
        return this.api.getProjectDtoList(workflowItemId, params, pageSize).pipe(map((result) => result.items));
    }

    public getProjectDtoListCsv(workflowItemId: number, paramsString?: string): Observable<APIResponse<DownloadURL>> {
        return this.api.getProjectDtoListCsv(workflowItemId, paramsString);
    }

    public getProjectDtoListTable(workflowItemId: number, paramsString?: string): Observable<DataSource> {
        return this.api.getProjectDtoListTable(workflowItemId, paramsString);
    }

    /*
     * APIs for ProjectAssetDto
     */
    public getProjectAssetDtoList(
        workflowItemId: number,
        filters?: Filter[],
        pageSize?: number,
    ): Observable<ProjectAssetDto[]> {
        return this.api.getProjectAssetDtoList(workflowItemId, filters, pageSize).pipe(map((result) => result.items));
    }

    public getProjectAssetsDtoListCsv(
        workflowItemId: number,
        filterParams?: Filter[],
    ): Observable<APIResponse<DownloadURL>> {
        return this.api.getProjectAssetsDtoListCsv(workflowItemId, filterParams);
    }
    public getBulkProjectAssetsDtoListCsv(
        workflowItemId: number,
        paramsString: string,
    ): Observable<APIResponse<DownloadURL>> {
        return this.api.getBulkProjectAssetsDtoListCsv(workflowItemId, paramsString);
    }

    public getProjectAssetsDtoListTable(workflowItemId: number, filters?: Filter[]): Observable<DataSource> {
        return this.api.getProjectAssetDtoListTable(workflowItemId, filters);
    }

    /*
     * APIs for project scope
     */
    public getAssetScopeFilters(): Observable<APIResponse<TableFilterConfig[]>> {
        return this.api.getAssetScopeFilters();
    }

    public getAssetFilterFieldOptions(
        workflowItemId: number,
        filterOption: FilterFieldOptionRequest,
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.getAssetFilterFieldOptions(workflowItemId, filterOption);
    }

    public getAssetsByFiltersListTable(workflowItemId: number, filters: Filter[]): Observable<DataSource> {
        return this.api.getAssetsByFiltersListTable(workflowItemId, filters);
    }

    public getAssetsByFiltersByProjectListTable(
        workflowItemId: number,
        projectId: number,
        filters: Filter[],
        includeBoundary: Boolean,
    ): Observable<DataSource> {
        return this.api.getAssetsByFiltersByProjectListTable(workflowItemId, projectId, filters, includeBoundary);
    }

    public getAssetsByFiltersListCsv(
        workflowItemId: number,
        filters: DataTableParam,
    ): Observable<APIResponse<DownloadURL>> {
        return this.api.getAssetsByFiltersListCsv(workflowItemId, filters);
    }
    public getAssetsByFiltersByProjectListCsv(
        workflowItemId: number,
        projectId: number,
        filters: DataTableParam,
    ): Observable<APIResponse<DownloadURL>> {
        return this.api.getAssetsByFiltersByProjectListCsv(workflowItemId, projectId, filters);
    }

    /*
     * APIs for project-modifying actions
     */
    public removeProjectOutlierByProjectId(
        workflowItemId: number,
        projectId: number,
    ): Observable<APIResponse<boolean>> {
        return this.api.removeProjectOutlierByProjectId(workflowItemId, projectId);
    }

    public generateProjectBoundaryByProjectId(
        workflowItemId: number,
        projectId: number,
    ): Observable<APIResponse<boolean>> {
        return this.api.generateProjectBoundaryByProjectId(workflowItemId, projectId);
    }

    /*
     * Spatial Card
     */
    public getGeospatialCardDataByProject(
        projectId: number,
        request: SpatialCardMetricRequest,
    ): Observable<APIResponse<SpatialCardResponseMetric>> {
        return this.api.getGeospatialCardDataByProject(projectId, request);
    }

    /*
     * Other support APIs
     */
    public projectNameExistsValidator(projectName: string): Observable<APIResponse<boolean>> {
        return this.api.projectNameExistsValidator(projectName);
    }

    public getProjectExtraColumns(): Observable<APIResponse<AssetColumn[]>> {
        return this.api.getProjectExtraColumns();
    }

    public getProjectAssetExtraColumns(): Observable<APIResponse<AssetColumn[]>> {
        return this.api.getProjectAssetExtraColumns();
    }

    public getProjectScenarioOptions(): Observable<APIResponse<AlternativeScenarioOption[]>> {
        return this.api.getProjectScenarioOptions();
    }

    /*
     * Helpers - call other APIs
     */

    public getProjectsByProgram(programId: number): Observable<Project[]> {
        const filter: Filter = {
            fieldKey: 'programId',
            fieldType: FilterFieldType.LONG,
            operator: FilterOperatorType.EQUAL,
            value: programId,
        };
        return this.getProjectList([filter], 100);
    }

    public getProjectDtoListCsvByProgram(
        workflowItemId: number,
        programId: number,
    ): Observable<APIResponse<DownloadURL>> {
        const filters: Filter[] = [
            {
                fieldKey: 'programId',
                fieldType: FilterFieldType.LONG,
                operator: FilterOperatorType.EQUAL,
                value: programId,
            },
        ];
        return this.api.getProjectDtoListCsv(workflowItemId, TableService.createFiltersParamsString(filters));
    }

    public getProjectDtoListCsvBySegment(
        workflowItemId: number,
        segmentId: number,
    ): Observable<APIResponse<DownloadURL>> {
        const filters: Filter[] = [
            {
                fieldKey: 'segmentId',
                fieldType: FilterFieldType.LONG,
                operator: FilterOperatorType.EQUAL,
                value: segmentId,
            },
        ];

        return this.api.getProjectDtoListCsv(workflowItemId, TableService.createFiltersParamsString(filters));
    }

    public projectAssetsDtoListTableByProject(workflowItemId: number, projectId: number): Observable<DataSource> {
        const filter: Filter = {
            fieldKey: 'projectId',
            fieldType: FilterFieldType.LONG,
            operator: FilterOperatorType.EQUAL,
            value: projectId,
        };
        return this.getProjectAssetsDtoListTable(workflowItemId, [filter]);
    }

    public projectAssetsDtoListCsvByProject(
        workflowItemId: number,
        projectId: number,
    ): Observable<APIResponse<DownloadURL>> {
        const filter: Filter = {
            fieldKey: 'projectId',
            fieldType: FilterFieldType.LONG,
            operator: FilterOperatorType.EQUAL,
            value: projectId,
        };
        return this.getProjectAssetsDtoListCsv(workflowItemId, [filter]);
    }

    // TODO: this is being removed and refactored into alternatives
    getEconomicsOverTime(id: number, year: number): Observable<APIResponse<EconomicsOverTimeResponse>> {
        return this.api.getEconomicsOverTime(id, year);
    }
    findProjectCostAnalysis(id: number): Observable<APIResponse<ProjectCostAnalysis>> {
        return this.api.findProjectCostAnalysis(id);
    }
    downloadCostAnalysisListDtoCsv(projectId: number): Observable<APIResponse<DownloadURL>> {
        return this.api.downloadCostAnalysisListDtoCsv(projectId);
    }
}
